import { useEffect, useState } from 'react'

import { useBridge } from '@onl-web/shared/bridge'

import { setToken } from '@/apis/tokenStore'
import { IS_MOCK } from '@/constants'

/**
 * service-area 인증 초기화.
 *
 * gacha의 useInitAuth와 차이:
 *  - 토큰 실패 시 exitWebView 안 함 — 비회원 모드로 진행 허용
 *  - 광역권역 조회는 시스템 인증(x-api-key)으로 동작하므로 토큰 없어도 메인 페이지는 그대로 노출
 *  - 검색 인풋(HeroSection의 SearchField)만 토큰 유무로 분기 (page에서 hasToken() 체크)
 *
 * 흐름:
 *  - mock 모드: 더미 토큰으로 회원 모드 시뮬레이션
 *  - 실제 모드: bridge.getToken 시도 → 성공이면 회원 / 실패·timeout·빈 토큰이면 비회원
 */

type AuthStatus = 'loading' | 'ready'
const TOKEN_TIMEOUT_MS = 5000

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  Promise.race([promise, new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))])

export function useInitAuth() {
  const { isReady, callHandler } = useBridge()
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading')

  useEffect(() => {
    if (IS_MOCK) {
      setToken('mock-token')
      // 동기 setState — react-hooks/set-state-in-effect는 외부 시스템 동기화 패턴(IS_MOCK
      // 빌드 플래그)을 분간 못 함. .then()/.catch() 비동기 콜백의 setAuthStatus는 이미
      // 비동기 boundary 너머라 룰이 잡지 않음.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAuthStatus('ready')
      return
    }

    if (!isReady) return

    let cancelled = false

    withTimeout(callHandler('getToken'), TOKEN_TIMEOUT_MS)
      .then(({ token, bottom }) => {
        if (cancelled) return
        if (token) setToken(token)
        if (bottom !== undefined && bottom !== null) {
          document.documentElement.style.setProperty('--safe-bottom', `${bottom}px`)
        }
        setAuthStatus('ready')
      })
      .catch((error) => {
        // 토큰 못 받았어도 페이지는 계속 — 비회원 모드로 진행
        if (cancelled) return
        if (import.meta.env.DEV) {
          console.warn('[useInitAuth] getToken failed → guest mode', error)
        }
        setAuthStatus('ready')
      })

    return () => {
      cancelled = true
    }
  }, [isReady, callHandler])

  return { authStatus }
}
